/** Sports available for match-official registration (UI; API later). */
export const OFFICIAL_SPORTS = Object.freeze([
  {
    slug: "football",
    name: "Football",
    icon: "fa-solid fa-futbol",
    accent: "#009700",
    description: "11-a-side, 7-a-side, and small-sided fixtures across Kenya.",
  },
  {
    slug: "basketball",
    name: "Basketball",
    icon: "fa-solid fa-basketball",
    accent: "#e67e22",
    description: "5v5 full court, 3v3, school leagues, and recreational runs.",
  },
  {
    slug: "rugby",
    name: "Rugby",
    icon: "fa-solid fa-football",
    accent: "#1b5e20",
    description: "XV, 7s, 10s, schools, and club friendlies.",
  },
  {
    slug: "volleyball",
    name: "Volleyball",
    icon: "fa-solid fa-volleyball",
    accent: "#00838f",
    description: "Indoor 6v6, beach, and recreational tournaments.",
  },
  {
    slug: "tennis",
    name: "Tennis",
    icon: "fa-solid fa-table-tennis-paddle-ball",
    accent: "#2e7d32",
    description: "Singles, doubles, club ladders, and junior events.",
  },
]);

/**
 * Official roles shown after a sport is selected.
 * `id` is the value stored in the form (API payload later).
 */
export const OFFICIAL_TYPES_BY_SPORT = Object.freeze({
  football: [
    {
      id: "referee",
      label: "Referee",
      description: "Center referee — full authority on the pitch for the full match.",
      icon: "fa-solid fa-scale-balanced",
    },
    {
      id: "assistant_referee",
      label: "Assistant Referee",
      description: "Lines on the touchline; offside and out-of-play decisions.",
      icon: "fa-solid fa-flag",
    },
    {
      id: "fourth_official",
      label: "Fourth Official",
      description: "Bench-side official — substitutions, added time, and technical area.",
      icon: "fa-solid fa-clipboard-list",
    },
    {
      id: "match_commissioner",
      label: "Match Commissioner",
      description: "Oversees fixture logistics, discipline reports, and match documentation.",
      icon: "fa-solid fa-user-tie",
    },
  ],
  basketball: [
    {
      id: "referee",
      label: "Referee",
      description: "On-court officiating — fouls, violations, and game flow.",
      icon: "fa-solid fa-scale-balanced",
    },
    {
      id: "umpire_table",
      label: "Table Umpire",
      description: "Scoresheet, fouls, timeouts, and bench communication.",
      icon: "fa-solid fa-table",
    },
    {
      id: "scorekeeper",
      label: "Scorekeeper",
      description: "Tracks score, period, and team fouls on the official sheet.",
      icon: "fa-solid fa-pen-to-square",
    },
    {
      id: "timekeeper",
      label: "Timekeeper",
      description: "Game clock, shot clock coordination, and period breaks.",
      icon: "fa-solid fa-stopwatch",
    },
    {
      id: "shot_clock_operator",
      label: "Shot Clock Operator",
      description: "Operates shot clock where equipment is available.",
      icon: "fa-solid fa-clock",
    },
  ],
  rugby: [
    {
      id: "referee",
      label: "Referee",
      description: "Primary match official — scrums, rucks, and foul play.",
      icon: "fa-solid fa-scale-balanced",
    },
    {
      id: "assistant_referee",
      label: "Assistant Referee (Touch Judge)",
      description: "Touchline, kicks at goal, and forward passes.",
      icon: "fa-solid fa-flag",
    },
    {
      id: "tmo",
      label: "Television Match Official (TMO)",
      description: "Video review where broadcast or club replay is available.",
      icon: "fa-solid fa-tv",
    },
    {
      id: "reserve_official",
      label: "Reserve Official",
      description: "Standby cover for injury or absence of appointed crew.",
      icon: "fa-solid fa-user-shield",
    },
  ],
  volleyball: [
    {
      id: "first_referee",
      label: "1st Referee",
      description: "Primary authority on the net and playing actions.",
      icon: "fa-solid fa-scale-balanced",
    },
    {
      id: "second_referee",
      label: "2nd Referee",
      description: "Net faults, rotations, and support to the 1st referee.",
      icon: "fa-solid fa-user-check",
    },
    {
      id: "line_judge",
      label: "Line Judge",
      description: "Ball in/out and touch calls along assigned lines.",
      icon: "fa-solid fa-minus",
    },
    {
      id: "scorer",
      label: "Scorer",
      description: "Official scoresheet, line-ups, and substitutions.",
      icon: "fa-solid fa-pen-to-square",
    },
    {
      id: "libero_tracker",
      label: "Libero Tracker",
      description: "Tracks libero replacements on the scoresheet.",
      icon: "fa-solid fa-list-ol",
    },
  ],
  tennis: [
    {
      id: "chair_umpire",
      label: "Chair Umpire",
      description: "Overrules line calls, manages pace, and announces score.",
      icon: "fa-solid fa-chair",
    },
    {
      id: "line_umpire",
      label: "Line Umpire",
      description: "Calls balls in or out on assigned lines.",
      icon: "fa-solid fa-eye",
    },
    {
      id: "roving_umpire",
      label: "Roving Umpire",
      description: "Moves between courts at club or junior events.",
      icon: "fa-solid fa-person-walking",
    },
    {
      id: "referee",
      label: "Tournament Referee",
      description: "On-site authority for rules, scheduling, and disputes.",
      icon: "fa-solid fa-gavel",
    },
  ],
});

export const OFFICIAL_EXPERIENCE_LEVELS = Object.freeze([
  { value: "beginner", label: "Beginner (school & friendly games)" },
  { value: "club", label: "Club / county league" },
  { value: "regional", label: "Regional or national association" },
  { value: "federation", label: "Federation-licensed / professional track" },
]);

export const OFFICIAL_AVAILABILITY_DAYS = Object.freeze([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

export const OFFICIAL_MIN_FEE_OPTIONS = Object.freeze([
  { value: "1000", label: "From KSh 1,000 per match" },
  { value: "1500", label: "From KSh 1,500 per match" },
  { value: "2500", label: "From KSh 2,500 per match" },
  { value: "negotiable", label: "Negotiable — discuss with teams" },
]);

export const OFFICIAL_REGISTER_PATH = "/ipromiseafrica/matchup/officials/register";
export const OFFICIAL_REGISTER_ROUTE_NAME = "MatchOfficialRegistration";

export const OFFICIAL_DASHBOARD_PATH = "/ipromiseafrica/matchup/officials/dashboard";
export const OFFICIAL_DASHBOARD_ROUTE_NAME = "MatchOfficialDashboard";

export const OFFICIAL_DASHBOARD_API = Object.freeze({
  me: "/api/match-officials/me",
  stats: "/api/match-officials/dashboard/stats",
  confirmedMatches: "/api/match-officials/dashboard/confirmed-matches",
  liveMatches: "/api/match-officials/dashboard/live-matches",
  tournamentFixtures: "/api/match-officials/dashboard/tournament-fixtures",
  history: "/api/match-officials/dashboard/history",
  acceptAssignment: "/api/match-officials/dashboard/assignments/accept",
  endMatch: (matchId) => `/api/match-officials/dashboard/matches/${matchId}/end`,
  manualScore: (matchId) => `/api/match-officials/dashboard/matches/${matchId}/manual-score`,
});

export const OFFICIAL_TOURNAMENT_API = Object.freeze({
  list: "/api/match-officials/tournaments",
  detail: (tournamentNumber) => `/api/match-officials/tournaments/${tournamentNumber}`,
  claim: (tournamentNumber) => `/api/match-officials/tournaments/${tournamentNumber}/claim`,
});
